import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TwoFactorSetup from './TwoFactorSetup';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { toast } from "sonner";
import { Shield, ShieldCheck, ShieldX, Settings } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TwoFactorManagement = ({ user }) => {
    const [is2FAEnabled, setIs2FAEnabled] = useState(false);
    const [showSetup, setShowSetup] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isDisabling, setIsDisabling] = useState(false);

    useEffect(() => {
        checkTwoFactorStatus();
    }, []);

    const checkTwoFactorStatus = async () => {
        try {
            const response = await axios.get(`${API}/auth/user/status?user_id=${user.id}`);
            setIs2FAEnabled(response.data.otp_enabled || false);
        } catch (error) {
            console.error('Failed to check 2FA status:', error);
            toast.error('Gabim në kontrollimin e statusit të 2FA');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSetupComplete = (success) => {
        if (success) {
            setIs2FAEnabled(true);
            setShowSetup(false);
            toast.success('2FA u konfigurua me sukses!');
        }
    };

    const handleDisable2FA = async () => {
        const confirmed = window.confirm(
            'Jeni i sigurt që dëshironi të çaktivizoni two-factor authentication? Kjo do ta bëjë llogarinë tuaj më pak të sigurt.'
        );
        
        if (!confirmed) {
            return;
        }

        setIsDisabling(true);
        
        try {
            await axios.post(`${API}/auth/otp/disable`, {
                user_id: user.id
            });
            
            setIs2FAEnabled(false);
            toast.success('2FA u çaktivizua me sukses');
        } catch (error) {
            console.error('Failed to disable 2FA:', error);
            toast.error('Gabim në çaktivizimin e 2FA. Provoni përsëri.');
        } finally {
            setIsDisabling(false);
        }
    };

    if (isLoading) {
        return (
            <Card>
                <CardContent className="text-center p-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Duke ngarkuar statusin e 2FA...</p>
                </CardContent>
            </Card>
        );
    }

    if (showSetup) {
        return (
            <div className="space-y-4">
                <Button 
                    variant="outline" 
                    onClick={() => setShowSetup(false)}
                    className="mb-4"
                >
                    ← Kthehu prapa
                </Button>
                <TwoFactorSetup
                    userId={user.id}
                    onSetupComplete={handleSetupComplete}
                />
            </div>
        );
    }

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center space-x-3">
                    <Shield className="w-6 h-6 text-blue-600" />
                    <div>
                        <CardTitle>Two-Factor Authentication</CardTitle>
                        <CardDescription>
                            Rrisni sigurinë e llogarisë tuaj me 2FA
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                        {is2FAEnabled ? (
                            <ShieldCheck className="w-8 h-8 text-green-600" />
                        ) : (
                            <ShieldX className="w-8 h-8 text-red-500" />
                        )}
                        <div>
                            <p className="font-medium">
                                Statusi: 
                                <span className={`ml-2 font-semibold ${is2FAEnabled ? 'text-green-600' : 'text-red-600'}`}>
                                    {is2FAEnabled ? 'I Aktivizuar' : 'I Çaktivizuar'}
                                </span>
                            </p>
                            <p className="text-sm text-gray-600">
                                {is2FAEnabled 
                                    ? 'Llogaria juaj është e mbrojtur me 2FA'
                                    : 'Aktivizoni 2FA për siguri të shtuar'
                                }
                            </p>
                        </div>
                    </div>
                </div>

                {!is2FAEnabled ? (
                    <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <h4 className="font-medium text-blue-900 mb-2">Përfitimet e 2FA:</h4>
                            <ul className="text-sm text-blue-800 space-y-1">
                                <li>• Siguri e shtuar për llogarinë tuaj</li>
                                <li>• Mbrojtje nga hyrjet e paautorizuara</li>
                                <li>• Përdorim i aplikacioneve authenticator</li>
                                <li>• Standard i industrisë për siguri</li>
                            </ul>
                        </div>
                        
                        <Button
                            onClick={() => setShowSetup(true)}
                            className="w-full"
                        >
                            <Settings className="w-4 h-4 mr-2" />
                            Aktivizo 2FA
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <h4 className="font-medium text-green-900 mb-2">2FA është Aktiv</h4>
                            <p className="text-sm text-green-800">
                                Two-factor authentication është i aktivizuar. Përdorni aplikacionin tuaj 
                                authenticator për të gjeneruar kode kur hyni në llogari.
                            </p>
                        </div>
                        
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <h4 className="font-medium text-yellow-900 mb-2">⚠️ Kujdes</h4>
                            <p className="text-sm text-yellow-800 mb-3">
                                Çaktivizimi i 2FA do ta bëjë llogarinë tuaj më pak të sigurt. 
                                Sigurohuni që keni akses në aplikacionin authenticator përpara se ta çaktivizoni.
                            </p>
                        </div>
                        
                        <Button
                            variant="destructive"
                            onClick={handleDisable2FA}
                            disabled={isDisabling}
                            className="w-full"
                        >
                            {isDisabling ? 'Duke çaktivizuar...' : 'Çaktivizo 2FA'}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default TwoFactorManagement;